import { MessageBox, Tabs } from "@jutro/ui";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import ReactModal from "react-modal";
import {
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmNonWorkingPeriod,
} from "lib/graphql/megaSchema";
import { useVojevodshipOptions } from "lib/hooks/useVojevodshipOptions";
import { NonWorkingPeriodActionsModal } from "views/StaffSchedule/StaffScheduleCreator/components/Modals/NonWorkingPeriodActionsModal";
import { ShiftActionsModal } from "views/StaffSchedule/StaffScheduleCreator/components/Modals/ShiftActionsModal";
import {
  SlotActionModalMode,
  StaffOption,
  isNonWorkingPeriod,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import { StaffScheduleDataTestId } from "views/StaffSchedule/StaffScheduleDataTestId";

type Props = {
  staffOptions: OptionWithSideElements<StaffOption>[];
  isModalOpen: boolean;
  selectedBlockStaffId: string;
  selectedDay?: Dayjs;
  startDate: Dayjs;
  modalMode: SlotActionModalMode;
  selectedBlock:
    | EdmMedicalAppShiftWithoutVisitFragment
    | EdmNonWorkingPeriod
    | undefined;
  selectedOrganizationId: string;
  onCloseModal: () => void;
};

const pillOptions = [
  { label: "Wizyty", value: "VISITS" },
  { label: "Inne", value: "OTHER" },
];

export const BlockActionModal = ({
  isModalOpen,
  staffOptions,
  modalMode,
  selectedBlock,
  startDate,
  selectedDay,
  selectedOrganizationId,
  selectedBlockStaffId,
  onCloseModal,
}: Props) => {
  const { organizationsClinicsOptions } = useVojevodshipOptions();

  const [pillValue, setPillValue] = useState(
    isNonWorkingPeriod(selectedBlock)
      ? { label: "Inne", value: "OTHER" }
      : {
          label: "Wizyty",
          value: "VISITS",
        },
  );

  const isEditMode = modalMode === "edit";
  const isCreateMode = modalMode === "create";

  const { selectedShift, selectedNonWorkingPeriod } = useMemo(() => {
    if (isNonWorkingPeriod(selectedBlock)) {
      return {
        selectedShift: undefined,
        selectedNonWorkingPeriod: selectedBlock,
      };
    }

    return {
      selectedShift: selectedBlock,
      selectedNonWorkingPeriod: undefined,
    };
  }, [selectedBlock]);

  return (
    <ReactModal
      data-testId={StaffScheduleDataTestId.SlotActionModal}
      isOpen={isModalOpen}
      shouldCloseOnEsc
      onRequestClose={onCloseModal}
      className="flex max-h-screen flex-col items-center overflow-y-auto"
      ariaHideApp={false}
      shouldCloseOnOverlayClick
      overlayClassName="fixed w-full h-screen top-0 left-0 z-50 bg-black/60 flex justify-center items-center"
    >
      <div className="text-jutro-new-warm-gray-800 flex w-[912px] flex-col justify-between gap-4 rounded-lg bg-white px-4 py-6">
        <h4 className="text-jutro-new-warm-gray-700 text-lg font-bold">
          {isEditMode ? "Podgląd" : "Utwórz zmianę"}
        </h4>
        {!isEditMode && (
          <Tabs
            value={pillValue}
            options={pillOptions}
            backgroundColor="gray"
            onChange={(option) => setPillValue(option)}
          />
        )}
        <MessageBox text="Pola oznaczone * są obowiązkowe" />

        <div
          className={`flex h-full flex-col gap-4 ${
            pillValue.value === "VISITS" ? "block" : "hidden"
          }`}
        >
          <ShiftActionsModal
            staffOptions={staffOptions}
            organizationsClinicsOptions={organizationsClinicsOptions}
            selectedShift={selectedShift}
            startDate={startDate}
            selectedDay={selectedDay}
            selectedOrganizationId={selectedOrganizationId}
            selectedBlockStaffId={selectedBlockStaffId}
            isEditMode={isEditMode}
            isCreateMode={isCreateMode}
            onCloseModal={onCloseModal}
          />
        </div>

        <div
          className={`flex h-full flex-col gap-4 ${
            pillValue.value === "OTHER" ? "block" : "hidden"
          }`}
        >
          <NonWorkingPeriodActionsModal
            staffOptions={staffOptions}
            selectedNonWorkingPeriod={selectedNonWorkingPeriod}
            isEditMode={isEditMode}
            isCreateMode={isCreateMode}
            selectedBlockStaffId={selectedBlockStaffId}
            selectedDay={selectedDay}
            startDate={startDate}
            onCloseModal={onCloseModal}
          />
        </div>
      </div>
    </ReactModal>
  );
};
